import type { ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ABSTIMMUNGSSTATUS_BESCHRIFTUNG } from '@mcikm-vote/shared';
import type { AbstimmungsstatusWert } from '@mcikm-vote/shared';

import { useAbstimmungen, useAbstimmungVerschieben } from '../../admin/abstimmungenApi';
import { useVeranstaltung } from '../../admin/veranstaltungenApi';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { StatusZeichen } from '../../components/StatusZeichen';
import type { Tonart } from '../../components/StatusZeichen';

/** Farbliche **und** textliche Unterscheidung — §21 verbietet rein farbbasierte Information. */
export function abstimmungsTonart(status: AbstimmungsstatusWert): Tonart {
  switch (status) {
    case 'geoeffnet':
      return 'erfolg';
    case 'bereit':
      return 'aktiv';
    case 'annulliert':
      return 'fehler';
    default:
      return 'neutral';
  }
}

/**
 * Abstimmungen einer Veranstaltung (§10).
 *
 * Die Reihenfolge ist die vom Administrator festgelegte, nicht die der Nummern: eine Nummer trägt
 * erst, wer schon einmal geöffnet war (§10.2).
 */
export function AbstimmungenSeite(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const veranstaltung = useVeranstaltung(id);
  const abstimmungen = useAbstimmungen(id);
  const verschieben = useAbstimmungVerschieben();

  // Umsortieren geht nur, solange eine Abstimmung noch nicht geöffnet war (§10.3) — und nur
  // innerhalb dieser Gruppe. Deshalb zählt für „ganz oben" und „ganz unten" die Stellung unter
  // den umsortierbaren Abstimmungen, nicht die Zeilennummer der Tabelle.
  const umsortierbar = (abstimmungen.data ?? [])
    .filter((eintrag) => eintrag.status === 'entwurf' || eintrag.status === 'bereit')
    .map((eintrag) => eintrag.id);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Abstimmungen</h1>
          {veranstaltung.data !== undefined && (
            <p className="text-text-gedaempft text-sm">
              <Link
                to={`/verwaltung/veranstaltungen/${id}`}
                className="hover:text-akzent underline"
              >
                {veranstaltung.data.bezeichnung}
              </Link>
            </p>
          )}
        </div>

        <Link
          to="neu"
          className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche flex items-center rounded-md px-4 font-medium"
        >
          Neue Abstimmung
        </Link>
      </div>

      {abstimmungen.isPending && <p className="text-text-gedaempft text-sm">Wird geladen …</p>}

      {abstimmungen.isError && <Fehlermeldung>{abstimmungen.error.message}</Fehlermeldung>}

      {verschieben.isError && <Fehlermeldung>{verschieben.error.message}</Fehlermeldung>}

      {abstimmungen.data?.length === 0 && (
        <Karte className="text-text-gedaempft text-sm">
          Für diese Veranstaltung ist noch keine Abstimmung angelegt. Eine Abstimmung wird als
          Entwurf vorbereitet, dann als „bereit" markiert und erst danach geöffnet.
        </Karte>
      )}

      {abstimmungen.data !== undefined && abstimmungen.data.length > 0 && (
        <div className="border-rand bg-flaeche overflow-x-auto rounded-xl border shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-rand text-text-gedaempft border-b text-left">
              <tr>
                <th className="p-3 font-medium">Nr.</th>
                <th className="p-3 font-medium">Überschrift</th>
                <th className="p-3 font-medium">Auswahl</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Ergebnis</th>
                <th className="p-3 font-medium">Reihenfolge</th>
              </tr>
            </thead>
            <tbody>
              {abstimmungen.data.map((abstimmung) => (
                <tr key={abstimmung.id} className="border-rand border-b last:border-b-0">
                  <td className="text-text-gedaempft p-3 tabular-nums">
                    {abstimmung.nummer ?? '—'}
                  </td>
                  <td className="p-3">
                    <Link
                      to={`/verwaltung/abstimmungen/${abstimmung.id}`}
                      className="hover:text-akzent font-medium underline"
                    >
                      {abstimmung.ueberschrift}
                    </Link>
                  </td>
                  <td className="text-text-gedaempft p-3">
                    {abstimmung.optionen.length} Möglichkeiten
                  </td>
                  <td className="p-3">
                    <StatusZeichen
                      beschriftung={ABSTIMMUNGSSTATUS_BESCHRIFTUNG[abstimmung.status]}
                      tonart={abstimmungsTonart(abstimmung.status)}
                    />
                  </td>
                  <td className="p-3">
                    {abstimmung.status === 'geschlossen' &&
                      (abstimmung.ergebnisVeroeffentlichtAm === null ? (
                        <StatusZeichen beschriftung="Nicht veröffentlicht" />
                      ) : (
                        <StatusZeichen beschriftung="Veröffentlicht" tonart="erfolg" />
                      ))}
                  </td>
                  <td className="p-3">
                    {umsortierbar.includes(abstimmung.id) && (
                      <div className="flex gap-1">
                        <Knopf
                          art="sekundaer"
                          className="min-h-0 px-3 py-1"
                          title="Nach oben verschieben"
                          aria-label={`„${abstimmung.ueberschrift}" nach oben verschieben`}
                          disabled={verschieben.isPending || umsortierbar[0] === abstimmung.id}
                          onClick={() => {
                            verschieben.mutate({ id: abstimmung.id, richtung: 'hoch' });
                          }}
                        >
                          ↑
                        </Knopf>
                        <Knopf
                          art="sekundaer"
                          className="min-h-0 px-3 py-1"
                          title="Nach unten verschieben"
                          aria-label={`„${abstimmung.ueberschrift}" nach unten verschieben`}
                          disabled={
                            verschieben.isPending ||
                            umsortierbar[umsortierbar.length - 1] === abstimmung.id
                          }
                          onClick={() => {
                            verschieben.mutate({ id: abstimmung.id, richtung: 'runter' });
                          }}
                        >
                          ↓
                        </Knopf>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
